import * as React from 'react';
import { SIDE_NAV_ID } from './side-nav-constants';
import { SideNavFrame } from './side-nav-frame';
import { getInertProps } from './side-nav-inert';
import type { ISideNavProps } from './types';
import { useSideNavModal } from './use-side-nav-modal';
import { cn } from '@utils';

const EXPANDED_WIDTH_CLASS = 'w-[23rem] min-w-[23rem] max-w-[23rem]';
const COLLAPSED_WIDTH_CLASS = 'w-[6rem] min-w-[6rem] max-w-[6rem]';

interface IGetSideNavClassNameParams {
	collapsed: boolean;
	isMobile: boolean;
	mobileOpen: boolean;
	className?: string;
}

function getSideNavClassName({ collapsed, isMobile, mobileOpen, className }: IGetSideNavClassNameParams): string {
	return cn(
		'font-sans antialiased relative flex h-full shrink-0 flex-col overflow-hidden bg-primary box-border',
		'transition-[width,min-width,max-width,transform] duration-300 ease-in-out',
		'[&_a]:!font-normal [&_a]:!no-underline',
		'[&_a[aria-current=page]_.menu-text]:!font-semibold',
		isMobile && mobileOpen ? EXPANDED_WIDTH_CLASS : collapsed ? COLLAPSED_WIDTH_CLASS : EXPANDED_WIDTH_CLASS,
		isMobile && 'fixed inset-y-0 left-0 z-[999] !h-dvh',
		isMobile && !mobileOpen && '-translate-x-full',
		isMobile && mobileOpen && 'translate-x-0 shadow-[4px_0_24px_rgba(0,0,0,0.25)]',
		className
	);
}

/**
 * Presentational app chrome shell for a left navigation rail.
 * App-specific content is provided via slots. On mobile, the open drawer is a modal dialog.
 */
export const SideNav: React.FC<ISideNavProps> = ({
	collapsed,
	isMobile,
	mobileOpen,
	onBackdropClick,
	header,
	collapsedHeader,
	controls,
	footer,
	children,
	className,
	id = SIDE_NAV_ID,
	'aria-label': ariaLabel,
	itemsAriaLabel,
	itemsAsList = true,
}) => {
	const isMobileDrawerClosed = isMobile && !mobileOpen;
	const isMobileDrawerOpen = isMobile && mobileOpen;
	const isChromeCollapsed = collapsed && !isMobileDrawerOpen;
	const modalRef = useSideNavModal({ enabled: isMobileDrawerOpen, onClose: onBackdropClick });

	const frame = (
		<SideNavFrame
			collapsed={isChromeCollapsed}
			isMobile={isMobile}
			header={header}
			collapsedHeader={collapsedHeader}
			controls={controls}
			footer={footer}
			itemsAriaLabel={itemsAriaLabel}
			itemsAsList={itemsAsList}
		>
			{children}
		</SideNavFrame>
	);

	const nav = (
		<nav
			id={id}
			data-testid="side-nav"
			aria-label={ariaLabel}
			aria-hidden={isMobileDrawerClosed || undefined}
			{...getInertProps(isMobileDrawerClosed)}
			className={getSideNavClassName({ collapsed, isMobile, mobileOpen, className })}
		>
			{frame}
		</nav>
	);

	if (!isMobileDrawerOpen) {
		return nav;
	}

	return (
		<div
			ref={modalRef}
			role="dialog"
			aria-modal="true"
			aria-label={ariaLabel}
			data-testid="side-nav-modal"
		>
			<div
				aria-hidden="true"
				data-testid="side-nav-backdrop"
				className="fixed inset-0 z-[998] bg-bgOverlay"
				onClick={onBackdropClick}
			/>
			{nav}
		</div>
	);
};

SideNav.displayName = 'SideNav';
